import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { ExternalLink, LayoutGrid } from "lucide-react";

import { OpportunityId } from "@goose/schema/Salesforce";

import { Breadcrumbs } from "~/components/admin/Breadcrumbs";

import {
  ContactIcon,
  EvaluationIcon,
  OpportunityIcon,
  RequirementSetIcon,
} from "~/components/admin/Icons";

import { PageHeader } from "~/components/admin/PageHeader";
import { StatCard } from "~/components/admin/StatCard";
import { QueryState } from "~/components/QueryState";
import { Button } from "~/components/ui/Button";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/opportunities/$opportunityId",
)({
  component: OpportunityPage,
  params: {
    parse: ({ opportunityId }) => {
      return {
        opportunityId: OpportunityId.parse(opportunityId),
      };
    },
  },
});

function OpportunityPage() {
  const trpc = useTRPC();
  const { organizationId, opportunityId } = Route.useParams();

  const organizationQuery = useQuery(
    trpc.salesforce.organizations.get.queryOptions({
      id: organizationId,
    }),
  );

  const requirementsCount = useQuery(
    trpc.opportunities.requirements.count.queryOptions({
      organizationId,
      opportunityId,
    }),
  );

  const contactsCount = useQuery(
    trpc.contacts.count.queryOptions({
      organizationId,
      opportunityId,
    }),
  );

  const evaluationsCount = useQuery(
    trpc.contacts.evaluations.count.queryOptions({
      organizationId,
      opportunityId,
    }),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Link to="/admin">Admin</Link>
            <Link to="/admin/organizations">Organizations</Link>
            <Link
              to="/admin/organizations/$organizationId"
              params={{ organizationId }}
            >
              {organizationQuery.data?.name ?? "Organization"}
            </Link>
            <span>{opportunityId}</span>
          </Breadcrumbs>
        }
        icon={OpportunityIcon}
        title="Opportunity"
        actions={
          <>
            <QueryState result={organizationQuery}>
              {({ url }) => (
                <Button
                  as="a"
                  variant="outline"
                  size="sm"
                  href={`${url}/${opportunityId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in Salesforce
                </Button>
              )}
            </QueryState>

            {organizationId === process.env.CONNECTED_ORGANIZATION_ID && (
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/opportunity/$opportunityId"
                  params={{ opportunityId }}
                >
                  <LayoutGrid className="mr-2 h-4 w-4" />
                  View in app
                </Link>
              </Button>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Link
          to="/admin/organizations/$organizationId/opportunities/$opportunityId/requirements"
          params={{ organizationId, opportunityId }}
          className="contents"
        >
          <StatCard
            label="Req Sets"
            value={requirementsCount.data ?? 0}
            icon={<RequirementSetIcon className="h-7 w-7" />}
            isLoading={requirementsCount.isLoading}
            error={requirementsCount.error}
            clickable
          />
        </Link>
        <Link
          to="/admin/organizations/$organizationId/opportunities/$opportunityId/contacts"
          params={{ organizationId, opportunityId }}
          className="contents"
        >
          <StatCard
            label="Contacts"
            value={contactsCount.data ?? 0}
            icon={<ContactIcon className="h-7 w-7" />}
            isLoading={contactsCount.isLoading}
            error={contactsCount.error}
            clickable
          />
        </Link>
        <StatCard
          label="Evaluations"
          value={evaluationsCount.data ?? 0}
          icon={<EvaluationIcon className="h-7 w-7" />}
          isLoading={evaluationsCount.isLoading}
          error={evaluationsCount.error}
        />
      </div>

      <Outlet />
    </div>
  );
}
